import React, { useEffect, useState } from "react"

import { Button, FormControl, Grid, MenuItem, Select, TextField } from "@material-ui/core"
import { Notification, Portlet } from "@/components"
import { ptBR } from "date-fns/locale"
import DateFnsUtils from "@date-io/date-fns"

import { TrackablelinkResponse, TrackableLinkUTMsReportResponse, Trackablelink, TrackableLinkUTMTypesResponse } from "@/protocols/trackableLinks"
import { DEFAULT_WHERE_DATA, DefaultWhereDataType } from "@/utils/pagination"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"

import TrackableLinkUTMMetricsSkeleton from "@/pages/Admin/Reports/UtmsReport/UtmsReportTable/UtmsReportTableSkeleton"
import UtmsReportTableUTMValue from "@/pages/Admin/Reports/UtmsReport/UtmsReportTable/UtmsReportTableUTMValue"
import DataTableWithPagination from "@/components/DataTableWithPagination"
import { TableColumn } from "@/components/DataTable"
import { fullDatetime } from "@/utils/time"
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers"

import SelectInput from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/SelectInput"
import useStyles from "@/pages/Admin/Reports/UtmsReport/UtmsReportTable/styles"

export type UtmsReportTableSearchFilters = {
	trackableLinkId: string | null
	searchByUTMType: string | null
	searchByUTMValue: string | null
	fromDate: Date | null
	toDate: Date | null
}

export type UtmsReportTableDataResponse = {
	type: string
	value: string
	clicksCount: number
	sentMessagesCount: number
	linksCount: number
	referenceTime: string
	createdAt: Date
}

type TrackableLinksData = {
	trackableLinks: Trackablelink[]
	count: number
}

type FormattedUtmsReportTableData = {
	utmsReport: UtmsReportTableDataResponse[]
	count: number
}

type FormattedUtmTypesData = {
	defaultUtmTypes: TrackableLinkUTMTypesResponse["defaultUtmTypes"]
	customUtmTypes: TrackableLinkUTMTypesResponse["customUtmTypes"]
}

const utmMetricsTableColumns: TableColumn<UtmsReportTableDataResponse>[] = [
	{ field: "type", label: "Tipo de UTM" },
	{ field: "value", label: "Valor", rowRender: (value) => <UtmsReportTableUTMValue utmValue={value as string} /> },
	{ field: "clicksCount", label: "Cliques" },
	{ field: "sentMessagesCount", label: "Mensagens enviadas" },
	{ field: "linksCount", label: "Links vinculados" },
	{ field: "createdAt", label: "Criado em", rowRender: (value) => fullDatetime(new Date(value)) }
]

const DEFAULT_VALUE_SELECT_LINKS_INPUT = "Todos os links"

const UtmsReportTable: React.FC = () => {
	const { triggerValidation, validation, clearValidation } = useValidation()

	const classes = useStyles()

	const [loading, setLoading] = useState(false)
	const [paginationState, setPaginationState] = useState<DefaultWhereDataType>(DEFAULT_WHERE_DATA)
	const [links, setLinks] = useState<TrackableLinksData>({
		trackableLinks: [],
		count: 0
	})
	const [trackableLinkUtmsReport, setTrackableLinkUtmsReport] = useState<FormattedUtmsReportTableData>({
		utmsReport: [],
		count: 0
	})
	const [trackableLinkUtmTypes, setTrackableLinkUtmTypes] = useState<FormattedUtmTypesData>({
		defaultUtmTypes: [],
		customUtmTypes: []
	})
	const [utmsReportSearchFilters, setUtmsReportSearchFilters] = useState<UtmsReportTableSearchFilters>({
		trackableLinkId: null,
		searchByUTMType: null,
		searchByUTMValue: null,
		fromDate: null,
		toDate: null
	})

	const handleGetUtmsReport = async () => {
		setLoading(true)
		try {
			const { data } = await ApiService.get<TrackableLinkUTMsReportResponse>("/trackable-links/utms/report", {
				params: {
					trackableLinkId: utmsReportSearchFilters.trackableLinkId === DEFAULT_VALUE_SELECT_LINKS_INPUT ? null : utmsReportSearchFilters.trackableLinkId,
					searchByUTMType: utmsReportSearchFilters.searchByUTMType,
					searchByUTMValue: utmsReportSearchFilters.searchByUTMValue,
					fromDate: utmsReportSearchFilters.fromDate,
					toDate: utmsReportSearchFilters.toDate,
					rowsPerPage: paginationState.rowsPerPage,
					page: paginationState.page
				}
			})

			setTrackableLinkUtmsReport({
				utmsReport: data.utmsReport,
				count: data.count
			})
		} catch (error) {
			Notification.error({ message: "Ocorreu um erro ao buscar as UTMs" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	const handleGetTrackableLinks = async () => {
		setLoading(true)
		try {
			const { data } = await ApiService.get<TrackablelinkResponse>("/trackable-links", {
				params: {
					rowsPerPage: paginationState.rowsPerPage,
					page: paginationState.page
				}
			})

			setLinks({
				trackableLinks: data.trackablelinks,
				count: data.count
			})

			return data
		} catch (error) {
			Notification.error({ message: "Ocorreu um erro ao buscar os links rastreáveis" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	const handleGetUtmTypes = async () => {
		setLoading(true)
		try {
			const { data } = await ApiService.get<TrackableLinkUTMTypesResponse>("/trackable-links/utm/types")

			setTrackableLinkUtmTypes({
				defaultUtmTypes: data.defaultUtmTypes,
				customUtmTypes: data.customUtmTypes
			})

			return data
		} catch (error) {
			Notification.error({ message: "Ocorreu um erro ao buscar os tipos de UTM" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	const handleChangeEditFilters = (data: Partial<UtmsReportTableSearchFilters>) => {
		setUtmsReportSearchFilters({
			...utmsReportSearchFilters,
			...data
		})
	}

	const handlePaginationState = (data: Partial<DefaultWhereDataType>) => {
		setPaginationState(lastState => ({
			...lastState,
			...data
		}))
	}

	const handleApplyFilters = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		clearValidation("searchByUTMValue")

		handlePaginationState({ page: DEFAULT_WHERE_DATA.page })
		await handleGetUtmsReport()
	}

	const selectInputOptions = trackableLinkUtmTypes.defaultUtmTypes.concat(trackableLinkUtmTypes.customUtmTypes).map(type => {
		return {
			id: type,
			name: type
		}
	})

	const selectInputOptionsGroupBy = (type: { name: string }) => {
		if (trackableLinkUtmTypes.defaultUtmTypes.includes(type.name)) {
			return "Padrões"
		}
		return "Customizadas"
	}

	useEffect(() => {
		handleGetUtmsReport()
		handleGetTrackableLinks()
		handleGetUtmTypes()
	}, [paginationState])

	return (
		<Grid container direction="column" spacing={3}>
			<Grid item>
				<Portlet>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Grid container direction="row" justifyContent="space-between">
								<form onSubmit={handleApplyFilters}>
									<Grid container spacing={1} xs={12}>
										<Grid item xs={2}>
											<FormControl fullWidth>
												<Select
													value={utmsReportSearchFilters.trackableLinkId ?? DEFAULT_VALUE_SELECT_LINKS_INPUT}
													variant="outlined"
													fullWidth
													onChange={
														({ target }) => handleChangeEditFilters({ trackableLinkId: target.value as string })
													}
												>
													<MenuItem value={DEFAULT_VALUE_SELECT_LINKS_INPUT}>Todos os links</MenuItem>
													{links.trackableLinks.map((link) => (
														<MenuItem key={link.id} value={link.id}>{link.title}</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>
										<Grid item xs={3}>
											<FormControl fullWidth>
												<SelectInput
													multiple={false}
													onChange={trackableLinkUtmType => handleChangeEditFilters({ searchByUTMType: trackableLinkUtmType?.name ?? null })}
													placeholder="Tipos de UTM"
													noOptionsText="Nenhum tipo de UTM encontrado"
													groupBy={(type => selectInputOptionsGroupBy(type))}
													options={selectInputOptions}
												/>
											</FormControl>
										</Grid>
										<Grid item xs={2}>
											<FormControl fullWidth>
												<TextField
													value={utmsReportSearchFilters.searchByUTMValue}
													onChange={
														({ target }) => handleChangeEditFilters({ searchByUTMValue: target.value?.trimStart() })
													}
													placeholder="Valor da UTM"
													fullWidth
													variant="outlined"
													helperText={validation.searchByUTMValue}
													error={!!validation.searchByUTMValue}
												/>
											</FormControl>
										</Grid>
										<Grid item xs={2}>
											<MuiPickersUtilsProvider
												locale={ptBR}
												utils={DateFnsUtils}
											>
												<KeyboardDatePicker
													okLabel="Pronto"
													cancelLabel="Cancelar"
													value={utmsReportSearchFilters.fromDate}
													variant="inline"
													inputVariant="outlined"
													onChange={(date) => handleChangeEditFilters({ fromDate: date })}
													placeholder="De"
													size="small"
													format="dd/MM/yyyy"
													InputAdornmentProps={{
														position: "start"
													}}
													InputProps={{
														classes: { root: classes.dateInput }
													}}
												/>
											</MuiPickersUtilsProvider>
										</Grid>
										<Grid item xs={2}>
											<MuiPickersUtilsProvider
												locale={ptBR}
												utils={DateFnsUtils}
											>
												<KeyboardDatePicker
													okLabel="Pronto"
													cancelLabel="Cancelar"
													value={utmsReportSearchFilters.toDate}
													variant="inline"
													inputVariant="outlined"
													onChange={(date) => handleChangeEditFilters({ toDate: date })}
													placeholder="Até"
													size="small"
													format="dd/MM/yyyy"
													InputAdornmentProps={{
														position: "start"
													}}
													InputProps={{
														classes: { root: classes.dateInput }
													}}
												/>
											</MuiPickersUtilsProvider>
										</Grid>
										<Grid item xs={1}>
											<Button
												variant="contained"
												color="primary"
												type="submit"
												className={classes.searchButton}
											>
												Buscar
											</Button>
										</Grid>
									</Grid>
								</form>
							</Grid>
						</Grid>

						{loading ? (
							<Grid item xs={12}>
								<TrackableLinkUTMMetricsSkeleton columnList={utmMetricsTableColumns} />
							</Grid>
						) : (
							<Grid item xs={12}>
								<DataTableWithPagination
									columns={utmMetricsTableColumns}
									data={trackableLinkUtmsReport.utmsReport}
									page={paginationState.page}
									rowsPerPage={paginationState.rowsPerPage}
									totalRows={trackableLinkUtmsReport.count}
									onPaginationChange={handlePaginationState}
								/>
							</Grid>
						)}
					</Grid>
				</Portlet>
			</Grid>
		</Grid>
	)
}

export default UtmsReportTable
